import type React from 'react'
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react'

interface OfflineContextType {
  isOffline: boolean
  modalVisible: boolean
  dismissModal: () => void
}

const OfflineContext = createContext<OfflineContextType | undefined>(undefined)

interface OfflineProviderProps {
  children: React.ReactNode
  autoDismissTimeout?: number
}

export const OfflineProvider: React.FC<OfflineProviderProps> = ({
  children,
  autoDismissTimeout = 10000,
}) => {
  const [isOffline, setIsOffline] = useState(!navigator.onLine)
  const [modalVisible, setModalVisible] = useState(false)
  const [hasSeenModal, setHasSeenModal] = useState(false)
  const autoDismissTimerRef = useRef<NodeJS.Timeout | null>(null)

  const dismissModal = useCallback(() => {
    setModalVisible(false)
    if (autoDismissTimerRef.current) {
      clearTimeout(autoDismissTimerRef.current)
      autoDismissTimerRef.current = null
    }
  }, [])

  // Handle online/offline events
  useEffect(() => {
    const handleOnline = () => {
      setIsOffline(false)
      setModalVisible(false)
      setHasSeenModal(false) // Reset so modal can show again if going offline later
      if (autoDismissTimerRef.current) {
        clearTimeout(autoDismissTimerRef.current)
        autoDismissTimerRef.current = null
      }
    }

    const handleOffline = () => {
      setIsOffline(true)
      if (!hasSeenModal) {
        setModalVisible(true)
        setHasSeenModal(true)
      }
    }

    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)

    // Check initial state
    if (!navigator.onLine && !hasSeenModal) {
      setModalVisible(true)
      setHasSeenModal(true)
    }

    return () => {
      window.removeEventListener('online', handleOnline)
      window.removeEventListener('offline', handleOffline)
    }
  }, [hasSeenModal])

  // Auto-dismiss timer
  useEffect(() => {
    if (modalVisible && autoDismissTimeout > 0) {
      autoDismissTimerRef.current = setTimeout(() => {
        setModalVisible(false)
      }, autoDismissTimeout)
    }

    return () => {
      if (autoDismissTimerRef.current) {
        clearTimeout(autoDismissTimerRef.current)
        autoDismissTimerRef.current = null
      }
    }
  }, [modalVisible, autoDismissTimeout])

  const value = {
    isOffline,
    modalVisible,
    dismissModal,
  }

  return (
    <OfflineContext.Provider value={value}>{children}</OfflineContext.Provider>
  )
}

export const useOffline = () => {
  const context = useContext(OfflineContext)
  if (!context) {
    throw new Error('useOffline must be used within an OfflineProvider')
  }
  return context
}
